import { useEffect, useRef, useState } from 'react';
import type { IUpdateStayInput } from '../../../__generated__/graphql';
import {
  useMyStayQuery,
  useStayPrivateDetailsQuery,
  useUpdateStayMutation,
} from '../../services/stayService/__generated__/queries.generated';
import {
  emptyStayForm,
  stayToForm,
  toUpdateInput,
  type StayForm,
  type StayFormKey,
  type StayListing,
} from './stayForm';
import { stayErrorMessage } from './stayErrors';

export interface SaveResult {
  ok: boolean;
  listing?: StayListing;
  /** Set when the API refused the save; problems found here are in `errors` instead. */
  message?: string;
  error?: unknown;
}

/**
 * Loads a stay for its host and keeps the editable form. `save(keys)` sends
 * only those fields (plus `extra`), shows client-side errors next to the
 * boxes first, and refreshes the form from what the API saved.
 */
export function useStayEditor(id?: string) {
  const skip = !id;
  const listingQuery = useMyStayQuery({
    variables: { id: id ?? '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const privateQuery = useStayPrivateDetailsQuery({
    variables: { id: id ?? '' },
    skip,
    fetchPolicy: 'network-only',
  });
  const [updateStay, { loading: saving }] = useUpdateStayMutation();
  const [form, setForm] = useState<StayForm>(emptyStayForm);
  const [errors, setErrors] = useState<Partial<Record<StayFormKey, string>>>({});
  const loadedFor = useRef<string | null>(null);

  const listing = listingQuery.data?.myStay ?? null;
  const privateValues = privateQuery.data?.stayPrivateDetails ?? null;

  // Fill the form once per listing, after the private details answered (or failed).
  useEffect(() => {
    if (!listing || privateQuery.loading || loadedFor.current === listing.id) return;
    loadedFor.current = listing.id;
    setForm(stayToForm(listing, privateValues));
  }, [listing, privateValues, privateQuery.loading]);

  const change = (patch: Partial<StayForm>) => {
    setForm(previous => ({ ...previous, ...patch }));
    setErrors(previous => {
      const next = { ...previous };
      Object.keys(patch).forEach(key => delete next[key as StayFormKey]);
      return next;
    });
  };

  /** Puts the form back to the saved listing (Cancel on the manage page). */
  const reset = () => {
    if (listing) setForm(stayToForm(listing, privateValues));
    setErrors({});
  };

  const save = async (
    keys: readonly StayFormKey[],
    extra: Partial<IUpdateStayInput> = {},
  ): Promise<SaveResult> => {
    if (!id) return { ok: false, message: 'Create the listing first' };
    const { input, errors: found } = toUpdateInput(form, keys);
    if (Object.keys(found).length) {
      // Shown next to each box; no banner message.
      setErrors(found);
      return { ok: false };
    }
    try {
      const { data } = await updateStay({ variables: { id, input: { ...input, ...extra } } });
      const saved = data?.updateStay;
      if (saved) {
        // Keep what the host typed for the private fields; the listing type has none.
        setForm(previous => ({
          ...stayToForm(saved, {
            checkInInstructions: previous.checkInInstructions,
            wifiName: previous.wifiName,
            wifiPassword: previous.wifiPassword,
          }),
        }));
      }
      setErrors({});
      return { ok: true, listing: saved };
    } catch (error) {
      return { ok: false, error, message: stayErrorMessage(error, 'Could not save. Try again.') };
    }
  };

  return {
    listing,
    loading: listingQuery.loading && !listing,
    loadError: listingQuery.error,
    refetch: listingQuery.refetch,
    form,
    errors,
    change,
    reset,
    save,
    saving,
  };
}
